import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  LocalHospital as MedicalIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { db, storage } from '../../firebase/config';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import { fetchMedicalTransportOrderById } from '../../redux/slices/medicalTransportOrdersSlice';
import { isCentralRole } from '../../redux/slices/authSlice';
import {
  APPROVAL_STATUS_OPTIONS,
  BILLING_STATUS_OPTIONS,
  CARE_LEVEL_OPTIONS,
  COPAYMENT_STATUS_OPTIONS,
  DOCUMENT_STATUS_OPTIONS,
  ELIGIBILITY_PROOF_TYPE_OPTIONS,
  MOBILITY_MARK_OPTIONS,
  PERSON_GROUP_STATUS_OPTIONS,
  ROUTE_SCOPE_OPTIONS,
  TREATMENT_TYPE_OPTIONS,
  formatDate,
  getOption
} from './medicalTransportOrderOptions';
import { SPECIAL_CAPABILITIES, VEHICLE_TYPES } from '../../config/vehicleTypes';

const StatusChip = ({ options, value }) => {
  const option = getOption(options, value);
  return (
    <Chip
      label={option.label}
      color={option.color || 'default'}
      size="small"
      variant={option.color === 'default' ? 'outlined' : 'filled'}
    />
  );
};

const DetailLine = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">
      {value || 'Nicht gesetzt'}
    </Typography>
  </Box>
);

const getLabel = (options, value) =>
  options.find(option => option.value === value)?.label || value || 'Nicht gesetzt';

const formatTripDirection = (order) => {
  if (order.outboundTrip && order.returnTrip) return 'Hinfahrt und Rückfahrt';
  if (order.outboundTrip) return 'Hinfahrt';
  if (order.returnTrip) return 'Rückfahrt';
  return 'Nicht gesetzt';
};

const RIDE_STATUS_LABELS = {
  open: 'Angefragt',
  dispatcher_pending: 'Fahrer angefragt',
  scheduled: 'Geplant',
  pre_accepted: 'Angenommen',
  accepted: 'Akzeptiert',
  arrived: 'Angekommen',
  in_progress: 'Zielfahrt',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
  cancelled_by_driver: 'Vom Fahrer storniert',
  paused: 'Pausiert'
};

const RIDE_STATUS_COLORS = {
  open: 'warning',
  dispatcher_pending: 'warning',
  scheduled: 'secondary',
  pre_accepted: 'info',
  accepted: 'info',
  arrived: 'primary',
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'error',
  cancelled_by_driver: 'error',
  paused: 'warning'
};

const COMPLETED_STATUSES = new Set(['completed']);
const CANCELLED_STATUSES = new Set(['cancelled', 'cancelled_by_driver']);
const PLANNED_STATUSES = new Set(['scheduled', 'pre_accepted', 'accepted', 'arrived', 'in_progress', 'open', 'dispatcher_pending']);

const MEDICAL_PROOF_LABELS = {
  missing: 'Fehlt',
  incomplete: 'Unvollständig',
  complete: 'Vollständig',
  waived: 'Nicht möglich',
  problem: 'Problem',
  rejected: 'Beanstandet'
};

const MEDICAL_PROOF_COLORS = {
  missing: 'warning',
  incomplete: 'warning',
  complete: 'success',
  waived: 'info',
  problem: 'error',
  rejected: 'error'
};

const MEDICAL_PROOF_TYPE_LABELS = {
  digital_signature: 'Signatur',
  paper_proof: 'Papier',
  facility_confirmation: 'Einrichtung',
  cannot_sign: 'Keine Unterschrift',
  no_show: 'No-Show',
  problem: 'Problem'
};

const toRideDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatRideDateTime = (value) => {
  const date = toRideDate(value);
  if (!date) return 'Nicht gesetzt';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const sortLinkedRides = (rides) => [...rides].sort((a, b) => {
  const aMs = toRideDate(a.scheduledTime || a.timestamp)?.getTime() || 0;
  const bMs = toRideDate(b.scheduledTime || b.timestamp)?.getTime() || 0;
  return aMs - bMs;
});

const MedicalTransportOrderDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currentOrder, isLoading, error } = useSelector(state => state.medicalTransportOrders);
  const currentUser = useSelector(state => state.auth.user);
  const [linkedRides, setLinkedRides] = useState([]);
  const [linkedRidesLoading, setLinkedRidesLoading] = useState(false);
  const [linkedRidesError, setLinkedRidesError] = useState(null);
  const [scanDownloadUrl, setScanDownloadUrl] = useState('');

  useEffect(() => {
    dispatch(fetchMedicalTransportOrderById(id));
  }, [dispatch, id]);

  useEffect(() => {
    let cancelled = false;

    const loadLinkedRides = async () => {
      if (!id || !currentUser) return;

      setLinkedRidesLoading(true);
      setLinkedRidesError(null);

      try {
        const ridesQuery = isCentralRole(currentUser)
          ? query(collection(db, 'rides'), where('medicalTransportOrderId', '==', id))
          : currentUser.operatorId
            ? query(collection(db, 'rides'), where('visibleToOperatorIds', 'array-contains', currentUser.operatorId))
            : null;

        if (!ridesQuery) {
          if (!cancelled) setLinkedRides([]);
          return;
        }

        const snapshot = await getDocs(ridesQuery);
        const rides = snapshot.docs
          .map(rideDoc => ({ id: rideDoc.id, ...rideDoc.data() }))
          .filter(ride => ride.medicalTransportOrderId === id);

        if (!cancelled) {
          setLinkedRides(sortLinkedRides(rides));
        }
      } catch (loadError) {
        console.error('Fehler beim Laden der verknuepften Fahrten:', loadError);
        if (!cancelled) {
          setLinkedRidesError(loadError.message);
        }
      } finally {
        if (!cancelled) {
          setLinkedRidesLoading(false);
        }
      }
    };

    loadLinkedRides();

    return () => {
      cancelled = true;
    };
  }, [currentUser, id]);

  useEffect(() => {
    let cancelled = false;

    const loadScanDownloadUrl = async () => {
      if (!currentOrder?.prescriptionScanRef) {
        setScanDownloadUrl('');
        return;
      }

      try {
        const url = await getDownloadURL(storageRef(storage, currentOrder.prescriptionScanRef));
        if (!cancelled) setScanDownloadUrl(url);
      } catch (loadError) {
        console.warn('Originalverordnung konnte nicht geladen werden:', loadError);
        if (!cancelled) setScanDownloadUrl('');
      }
    };

    loadScanDownloadUrl();

    return () => {
      cancelled = true;
    };
  }, [currentOrder?.prescriptionScanRef]);

  if (isLoading && !currentOrder) {
    return <Loading message="Verordnung wird geladen..." />;
  }

  if (error && !currentOrder) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Verordnung"
        message={error}
        onRetry={() => dispatch(fetchMedicalTransportOrderById(id))}
      />
    );
  }

  if (!currentOrder) {
    return (
      <ErrorMessage
        title="Verordnung nicht gefunden"
        message="Der Verordnungsfall konnte nicht geladen werden."
        onRetry={() => dispatch(fetchMedicalTransportOrderById(id))}
      />
    );
  }

  const treatment = getOption(TREATMENT_TYPE_OPTIONS, currentOrder.treatmentType);
  const routeScope = getOption(ROUTE_SCOPE_OPTIONS, currentOrder.routeScope);
  const copayment = getOption(COPAYMENT_STATUS_OPTIONS, currentOrder.copaymentStatus);
  const vehicleType = VEHICLE_TYPES[currentOrder.defaultVehicleType]?.description || 'Nicht gesetzt';
  const capabilities = (currentOrder.requiredCapabilities || [])
    .map(value => SPECIAL_CAPABILITIES[value] || value)
    .join(', ');
  const linkedRideStats = linkedRides.reduce((stats, ride) => {
    if (COMPLETED_STATUSES.has(ride.status)) {
      stats.completed += 1;
    } else if (CANCELLED_STATUSES.has(ride.status)) {
      stats.cancelled += 1;
    } else if (PLANNED_STATUSES.has(ride.status)) {
      stats.planned += 1;
    } else {
      stats.other += 1;
    }
    return stats;
  }, { planned: 0, completed: 0, cancelled: 0, other: 0 });
  const maxRides = Number(currentOrder.maxRides) || 0;
  const countedRides = linkedRideStats.completed + linkedRideStats.planned;
  const remainingRides = maxRides > 0 ? Math.max(maxRides - countedRides, 0) : null;
  const hasExceededQuota = maxRides > 0 && countedRides > maxRides;
  const eligibilitySnapshot = currentOrder.medicalTransportEligibilitySnapshot || {};
  const mobilityMarks = Array.isArray(eligibilitySnapshot.mobilityMarks)
    ? eligibilitySnapshot.mobilityMarks
    : [];
  const mobilityMarksText = mobilityMarks.length
    ? mobilityMarks.map(value => getLabel(MOBILITY_MARK_OPTIONS, value)).join(', ')
    : 'Nicht hinterlegt';

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Verordnungsdetails"
        description="Fachliche Grundlage für wiederkehrende Krankenfahrten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Krankenfahrten', path: '/krankenfahrten' },
          { label: currentOrder.patientName || 'Details', path: `/krankenfahrten/${id}` }
        ]}
        actionButton={{
          label: 'Bearbeiten',
          icon: <EditIcon />,
          path: `/krankenfahrten/${id}/bearbeiten`
        }}
        secondaryButtons={[
          {
            label: 'Erneuern',
            icon: <CopyIcon />,
            path: `/krankenfahrten/${id}/erneuern`
          }
        ]}
      />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <MedicalIcon color="primary" />
              <Typography variant="h5" fontWeight="bold">
                {currentOrder.patientName}
              </Typography>
            </Stack>
            <Typography color="text.secondary">
              {currentOrder.pickupAddress || 'Start unbekannt'} → {currentOrder.destinationAddress || 'Ziel unbekannt'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <StatusChip options={APPROVAL_STATUS_OPTIONS} value={currentOrder.approvalStatus} />
            <StatusChip options={PERSON_GROUP_STATUS_OPTIONS} value={currentOrder.personGroupStatus || 'unknown'} />
            <StatusChip options={DOCUMENT_STATUS_OPTIONS} value={currentOrder.documentStatus} />
            <StatusChip options={BILLING_STATUS_OPTIONS} value={currentOrder.billingStatus} />
          </Stack>
        </Stack>
      </Paper>

      {currentOrder.renewedByOrderId && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button component={Link} to={`/krankenfahrten/${currentOrder.renewedByOrderId}`} size="small">
              Öffnen
            </Button>
          }
        >
          Zu dieser Verordnung wurde bereits eine Folgeverordnung erstellt.
        </Alert>
      )}

      {currentOrder.renewedFromOrderId && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button component={Link} to={`/krankenfahrten/${currentOrder.renewedFromOrderId}`} size="small">
              Öffnen
            </Button>
          }
        >
          Diese Verordnung wurde aus einer bestehenden Verordnung erneuert.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Patient und Krankenkasse</Typography>
              <DetailLine label="Patient" value={currentOrder.patientName} />
              <DetailLine label="Geburtsdatum" value={formatDate(currentOrder.patientBirthDate)} />
              <DetailLine label="Telefon" value={currentOrder.patientPhone} />
              <DetailLine label="Krankenkasse" value={currentOrder.healthInsuranceName} />
              <DetailLine label="Versichertennummer" value={currentOrder.insuranceNumber} />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Genehmigung</Typography>
              <DetailLine label="Verordnungsnummer" value={currentOrder.prescriptionNumber} />
              <DetailLine label="Genehmigungsnummer" value={currentOrder.approvalNumber} />
              <DetailLine label="Personenkreis" value={getOption(PERSON_GROUP_STATUS_OPTIONS, currentOrder.personGroupStatus || 'unknown').label} />
              <DetailLine label="Merkzeichen" value={mobilityMarksText} />
              <DetailLine label="Pflegegrad" value={getLabel(CARE_LEVEL_OPTIONS, eligibilitySnapshot.careLevel || '')} />
              <DetailLine label="Dauerhafte Mobilitätsbeeinträchtigung" value={eligibilitySnapshot.permanentMobilityImpairment ? 'Ja' : 'Nein / nicht bestätigt'} />
              <DetailLine label="Nachweisart" value={getLabel(ELIGIBILITY_PROOF_TYPE_OPTIONS, eligibilitySnapshot.proofType || '')} />
              <DetailLine label="Quelle Personenkreis" value={currentOrder.personGroupSource || 'manual'} />
              <DetailLine label="Genehmigt bis" value={formatDate(currentOrder.approvalValidUntil)} />
              <DetailLine label="Genehmigung erforderlich" value={currentOrder.approvalRequired === false ? 'Nein' : 'Ja'} />
              {currentOrder.personGroupHint && (
                <Alert severity={currentOrder.personGroupStatus === 'eligible' ? 'success' : 'warning'} sx={{ mt: 1 }}>
                  {currentOrder.personGroupHint}
                </Alert>
              )}
              {eligibilitySnapshot.notes && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {eligibilitySnapshot.notes}
                </Alert>
              )}
              {currentOrder.approvalHint && (
                <Alert severity={currentOrder.approvalRequired === false ? 'info' : 'warning'} sx={{ mt: 1 }}>
                  {currentOrder.approvalHint}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {currentOrder.prescriptionScanRef && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Originalverordnung</Typography>
                <DetailLine label="Scan-ID" value={currentOrder.prescriptionScanId} />
                <DetailLine label="Storage-Pfad" value={currentOrder.prescriptionScanRef} />
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  href={scanDownloadUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={!scanDownloadUrl}
                >
                  Original anzeigen
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Zeitraum und Kontingent</Typography>
              <DetailLine label="Gültig ab" value={formatDate(currentOrder.validFrom)} />
              <DetailLine label="Gültig bis" value={formatDate(currentOrder.validUntil)} />
              <DetailLine label="Fahrten/Woche" value={currentOrder.ridesPerWeek ? `${currentOrder.ridesPerWeek}x` : '-'} />
              <DetailLine label="Gefahren / geplant" value={`${linkedRideStats.completed}/${linkedRideStats.planned}`} />
              <DetailLine label="Zählend / Maximal" value={`${countedRides}/${currentOrder.maxRides || 0}`} />
              <DetailLine label="Verbleibend" value={remainingRides === null ? 'Kein Kontingent hinterlegt' : String(remainingRides)} />
              {hasExceededQuota && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Das Kontingent ist durch gefahrene und geplante Fahrten überschritten.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Strecke und Behandlung</Typography>
              <DetailLine label="Streckenbindung" value={routeScope.label} />
              <DetailLine label="Behandlungstyp" value={treatment.label} />
              <DetailLine label="Verordnete Richtung" value={formatTripDirection(currentOrder)} />
              <DetailLine label="Startadresse" value={currentOrder.pickupAddress} />
              <DetailLine label="Ziel / Einrichtung" value={currentOrder.destinationAddress} />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Sonderbedarf</Typography>
              <DetailLine label="Standard-Fahrzeugtyp" value={vehicleType} />
              <DetailLine label="Spezialfähigkeiten" value={capabilities || 'Keine'} />
              <DetailLine label="Begleitperson erforderlich" value={currentOrder.escortRequired ? 'Ja' : 'Nein'} />
              <DetailLine label="Zuzahlung" value={copayment.label} />
              {currentOrder.copaymentHint && (
                <Alert severity={currentOrder.copaymentStatus === 'required' ? 'warning' : 'info'} sx={{ mt: 1 }}>
                  {currentOrder.copaymentHint}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Notizen</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {currentOrder.defaultNotes || 'Keine Notizen hinterlegt'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">Verknüpfte Fahrten</Typography>
            <Typography variant="body2" color="text.secondary">
              Alle Fahrten, die diesem Verordnungsfall zugeordnet wurden.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${linkedRideStats.planned} geplant/offen`} color="info" variant="outlined" />
            <Chip label={`${linkedRideStats.completed} gefahren`} color="success" variant="outlined" />
            <Chip label={`${linkedRideStats.cancelled} storniert`} color="error" variant="outlined" />
          </Stack>
        </Stack>

        {linkedRidesError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Verknüpfte Fahrten konnten nicht geladen werden: {linkedRidesError}
          </Alert>
        )}

        {linkedRidesLoading ? (
          <Loading message="Verknüpfte Fahrten werden geladen..." />
        ) : linkedRides.length === 0 ? (
          <Alert severity="info">
            Zu diesem Verordnungsfall sind noch keine Fahrten verknüpft.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Zeitpunkt</TableCell>
                  <TableCell>Strecke</TableCell>
                  <TableCell>Fahrer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Nachweis</TableCell>
                  <TableCell align="right">Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linkedRides.map((ride) => (
                  <TableRow key={ride.id} hover>
                    <TableCell>{formatRideDateTime(ride.scheduledTime || ride.timestamp)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{ride.start || 'Start unbekannt'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ride.destination || 'Ziel unbekannt'}
                      </Typography>
                    </TableCell>
                    <TableCell>{ride.driverName || 'Nicht zugewiesen'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={RIDE_STATUS_LABELS[ride.status] || ride.status || 'Unbekannt'}
                        color={RIDE_STATUS_COLORS[ride.status] || 'default'}
                        variant={RIDE_STATUS_COLORS[ride.status] ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          label={MEDICAL_PROOF_LABELS[ride.medicalTransportProofStatus || 'missing'] || ride.medicalTransportProofStatus || 'Fehlt'}
                          color={MEDICAL_PROOF_COLORS[ride.medicalTransportProofStatus || 'missing'] || 'default'}
                          variant="outlined"
                        />
                        {ride.medicalTransportProofType && (
                          <Chip
                            size="small"
                            label={MEDICAL_PROOF_TYPE_LABELS[ride.medicalTransportProofType] || ride.medicalTransportProofType}
                            color={ride.medicalTransportProofType === 'no_show' || ride.medicalTransportProofType === 'problem' ? 'error' : 'info'}
                            variant="outlined"
                          />
                        )}
                        {Array.isArray(ride.medicalTransportSignaturePoints) && ride.medicalTransportSignaturePoints.length > 0 && (
                          <Chip
                            size="small"
                            label="Signatur"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton component={Link} to={`/fahrten/${ride.id}`} size="small" aria-label="Fahrt öffnen">
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Aktionen
        </Typography>
        <Button component={Link} to="/fahrten/neu" variant="outlined">
          Fahrt oder Dauerauftrag erstellen
        </Button>
      </Paper>
    </Box>
  );
};

export default MedicalTransportOrderDetails;
